import { Phase, type SessionState, type Action } from './types.ts';

// Initial state
export const initialState: SessionState = {
  phase: Phase.ONBOARDING,
  sessionId: null,
  clipIndex: 0,
  silenceIndex: 0,
  insightIndex: 0,
  context: null,
  baselineMood: 5,
  baselineNote: '',
  clipCheckins: [],
  silenceCheckins: [],
  shiftChoice: null,
  reflections: null,
  computedMetrics: null,
  clipTimeline: [],
  clips: [],
  guidanceTemplates: [],
};

// Get next phase based on current phase and state
export function getNextPhase(state: SessionState): Phase {
  const { phase, clipIndex, insightIndex, silenceIndex } = state;

  switch (phase) {
    case Phase.ONBOARDING:
      return Phase.CONTEXT;

    case Phase.CONTEXT:
      return Phase.BASELINE;

    case Phase.BASELINE:
      return Phase.CLIP_PLAY;

    case Phase.CLIP_PLAY:
      return Phase.CLIP_CHECKIN;

    case Phase.CLIP_CHECKIN:
      if (clipIndex === 1) return Phase.CLIP_PLAY; // clip 2
      if (clipIndex === 2) return Phase.SILENCE;   // breathing pause 1
      if (clipIndex === 3) return Phase.CLIP_PLAY; // clip 4
      if (clipIndex === 4) return Phase.SILENCE;   // breathing pause 2
      if (clipIndex === 5) return Phase.REFLECTION;
      return phase;

    case Phase.SILENCE:
      return Phase.SILENCE_CHECKIN;

    case Phase.SILENCE_CHECKIN:
      if (silenceIndex === 1) return Phase.INSIGHT;
      if (silenceIndex === 2) return Phase.INSIGHT;
      return Phase.REFLECTION;

    case Phase.INSIGHT:
      // After insight 1 → shift choice
      if (insightIndex === 1) return Phase.SHIFT_CHOICE;
      // After insight 2 → final clip
      if (insightIndex === 2) return Phase.CLIP_PLAY;
      return phase;

    case Phase.SHIFT_CHOICE:
      return Phase.CLIP_PLAY;

    case Phase.REFLECTION:
      return Phase.SUMMARY;

    case Phase.SUMMARY:
      return Phase.GUIDANCE;

    case Phase.GUIDANCE:
      return Phase.ONBOARDING; // Allow restart

    default:
      return phase;
  }
}

// Reducer function for state updates
export function reducer(state: SessionState, action: Action): SessionState {
  switch (action.type) {
    case 'NEXT_PHASE': {
      const nextPhase = getNextPhase(state);
      let newState = { ...state, phase: nextPhase };

      // Increment counters based on phase
      if (nextPhase === Phase.CLIP_PLAY) {
        newState.clipIndex = state.clipIndex + 1;
      }
      if (nextPhase === Phase.SILENCE) {
        newState.silenceIndex = state.silenceIndex + 1;
      }
      if (nextPhase === Phase.INSIGHT) {
        newState.insightIndex = state.insightIndex + 1;
      }

      return newState;
    }

    case 'SET_CONTEXT':
      return {
        ...state,
        context: action.payload,
      };

    case 'SET_BASELINE': {
      const { mood, note } = action.payload;
      return {
        ...state,
        baselineMood: mood,
        baselineNote: note,
        clipTimeline: [
          {
            label: 'Baseline',
            mood,
            timestamp: Date.now(),
            type: 'baseline',
          },
        ],
      };
    }

    case 'ADD_CLIP_CHECKIN': {
      const checkin = {
        clipIndex: state.clipIndex,
        mood: action.payload.mood,
        timestamp: Date.now(),
        label: action.payload.label,
      };
      return {
        ...state,
        clipCheckins: [...state.clipCheckins, checkin],
        clipTimeline: [
          ...state.clipTimeline,
          {
            label: action.payload.label ?? `Clip ${state.clipIndex}`,
            mood: action.payload.mood,
            timestamp: checkin.timestamp,
            type: 'clip' as const,
            index: state.clipIndex,
          },
        ],
      };
    }

    case 'ADD_SILENCE_CHECKIN': {
      const checkin = {
        silenceIndex: state.silenceIndex,
        mood: action.payload.mood,
        timestamp: Date.now(),
      };
      return {
        ...state,
        silenceCheckins: [...state.silenceCheckins, checkin],
        clipTimeline: [
          ...state.clipTimeline,
          {
            label: `Silence ${state.silenceIndex}`,
            mood: action.payload.mood,
            timestamp: checkin.timestamp,
            type: 'silence' as const,
            index: state.silenceIndex,
          },
        ],
      };
    }

    case 'SET_SHIFT_CHOICE':
      return {
        ...state,
        shiftChoice: action.payload,
      };

    case 'SET_REFLECTIONS':
      return {
        ...state,
        reflections: action.payload,
      };

    case 'SET_COMPUTED_METRICS':
      return {
        ...state,
        computedMetrics: action.payload,
      };

    case 'SET_SESSION_ID':
      return {
        ...state,
        sessionId: action.payload,
      };

    case 'SET_CLIPS':
      return {
        ...state,
        clips: action.payload,
      };

    case 'SET_GUIDANCE_TEMPLATES':
      return {
        ...state,
        guidanceTemplates: action.payload,
      };

    case 'RESTORE_STATE':
      return action.payload;

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// Get session progress percentage (0-100)
export function getProgress(phase: Phase): number {
  const progressMap: Record<Phase, number> = {
    [Phase.ONBOARDING]: 0,
    [Phase.CONTEXT]: 5,
    [Phase.BASELINE]: 10,
    [Phase.CLIP_PLAY]: 15, // Will be refined based on clipIndex
    [Phase.CLIP_CHECKIN]: 20,
    [Phase.SILENCE]: 50,
    [Phase.SILENCE_CHECKIN]: 55,
    [Phase.INSIGHT]: 60,
    [Phase.SHIFT_CHOICE]: 45,
    [Phase.REFLECTION]: 85,
    [Phase.SUMMARY]: 92,
    [Phase.GUIDANCE]: 100,
  };

  return progressMap[phase] || 0;
}

// Validate that a phase transition is allowed
export function canTransition(_from: Phase, _to: Phase): boolean {
  // For now, just check that we're not going backwards
  // In a production app, you'd have more sophisticated validation
  return true;
}
